import { logger } from '../lib/logger';
import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';

const AuthContext = createContext(null);

// Auth sepenuhnya cookie-based (HttpOnly). JavaScript tidak pernah membaca access token.
// Cookie ts_csrf (non-sensitive, double-submit token) dipakai sebagai penanda sesi untuk optimasi /auth/me.
const hasSessionMarker = () => document.cookie.split('; ').some((c) => c.startsWith('ts_csrf='));

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async (force = false) => {
    if (!force && !hasSessionMarker()) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const res = await api.get('/auth/me');
      setUser(res.data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const res = await api.post('/auth/login', { email, password });
    setUser(res.data.user);
    return res.data.user;
  };

  const register = async (payload) => {
    const res = await api.post('/auth/register', payload);
    setUser(res.data.user);
    return res.data.user;
  };

  const setSessionUser = (u) => setUser(u);

  const logout = async () => {
    try {
      await api.post('/auth/logout');
    } catch (e) {
      logger.warn('Logout request gagal, cookie dibersihkan client-side:', e?.message);
    }
    setUser(null);
  };

  const dashboardPath = (u) => {
    const role = (u || user)?.role;
    if (role === 'admin') return '/admin';
    if (role === 'seller') return '/seller/dashboard';
    return '/dashboard';
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, checkAuth, setSessionUser, dashboardPath }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
